import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  CheckCircle2, 
  XCircle,
  Mail,
  MessageSquare,
  Users,
  Calendar,
  FileText,
  Shield,
  Star,
  ArrowLeft,
  Zap
} from 'lucide-react';
import { apiRequest } from '@/config/api';
import { toast } from '../../components/ui/use-toast';

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hospitalSlug = searchParams.get('hospital');
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

  const plans = {
    silver: {
      name: 'Silver Plan',
      description: 'Essential doctor portal access for your practice',
      monthlyPrice: 2500,
      yearlyPrice: 2500 * 12, // Base yearly price (12 months)
      yearlyPlanDiscount: 10, // 10% discount on yearly plan
      icon: Shield,
      color: 'from-gray-600 to-gray-800',
      features: [
        { name: 'Doctor Portal Access', included: true },
        { name: 'Patient Management', included: true },
        { name: 'Appointment Scheduling', included: true },
        { name: 'Clinical Documentation', included: true },
        { name: 'Medical History Tracking', included: true },
        { name: 'Dental Examination Records', included: true },
        { name: 'Procedure Tracking', included: true },
        { name: 'Email to Patients', included: false },
        { name: 'SMS/WhatsApp Messages', included: false },
        { name: 'Patient Login Portal', included: false },
        { name: 'Patient Self-Booking', included: false },
        { name: 'Patient Record Access', included: false }
      ]
    },
    gold: {
      name: 'Gold Plan',
      description: 'Complete solution with patient engagement features',
      monthlyPrice: 3500,
      yearlyPrice: 3500 * 12, // Base yearly price (12 months)
      yearlyPlanDiscount: 15, // 15% discount on yearly plan
      icon: Star,
      color: 'from-yellow-500 to-orange-600',
      popular: true,
      features: [
        { name: 'Doctor Portal Access', included: true },
        { name: 'Patient Management', included: true },
        { name: 'Appointment Scheduling', included: true },
        { name: 'Clinical Documentation', included: true },
        { name: 'Medical History Tracking', included: true },
        { name: 'Dental Examination Records', included: true },
        { name: 'Procedure Tracking', included: true },
        { name: 'Email to Patients', included: true },
        { name: 'SMS/WhatsApp Messages', included: true },
        { name: 'Patient Login Portal', included: true },
        { name: 'Patient Self-Booking', included: true },
        { name: 'Patient Record Access', included: true }
      ]
    }
  };

  // Calculate discounted price for yearly billing
  const calculateYearlyPrice = (plan) => {
    let finalPrice = plan.yearlyPrice;
    let discountAmount = 0;
    let totalDiscountPercent = 0;

    if (plan.yearlyPlanDiscount && plan.yearlyPlanDiscount > 0) {
      // Apply yearly plan discount
      discountAmount = (finalPrice * plan.yearlyPlanDiscount) / 100;
      finalPrice = finalPrice - discountAmount;
      totalDiscountPercent = plan.yearlyPlanDiscount;
    }

    return {
      originalPrice: plan.yearlyPrice,
      finalPrice: Math.round(finalPrice),
      discountAmount: Math.round(discountAmount),
      totalDiscountPercent: Math.round(totalDiscountPercent * 10) / 10, // Round to 1 decimal
      monthlyEquivalent: Math.round(finalPrice / 12)
    };
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEnroll = async (planName) => {
    if (!hospitalSlug) {
      toast({
        title: 'Error',
        description: 'Hospital information not found',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const planType = planName.toUpperCase(); // 'SILVER' or 'GOLD'
      const paymentPlan = billingCycle === 'monthly' ? 'MONTHLY' : 'YEARLY';
      
      const response = await apiRequest('submit_enrollment_request.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_slug: hospitalSlug,
          plan_type: planType,
          payment_plan: paymentPlan
        })
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Enrollment request submitted successfully! Our team will review and approve your request shortly.',
        });
        // Optionally navigate back to hospital landing page
        setTimeout(() => {
          navigate(`/${hospitalSlug}`);
        }, 2000);
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to submit enrollment request',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit enrollment request. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Pricing Plans - Dental Hub</title>
        <meta name="description" content="Choose the perfect plan for your dental practice. Silver and Gold plans available with monthly or yearly billing." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Button
                variant="ghost"
                onClick={() => hospitalSlug ? navigate(`/${hospitalSlug}`) : navigate('/')}
                className="text-white hover:text-blue-400"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-2">
                <Shield className="h-6 w-6 text-blue-400" />
                <span className="text-xl font-bold gradient-text">Dental Hub</span>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text">
                Choose Your Plan
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Select the perfect plan for your dental practice
              </p>
              
              {/* Billing Cycle Toggle */}
              <div className="flex items-center justify-center gap-4 mb-12">
                <span className={`text-lg font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-gray-400'}`}>
                  Monthly
                </span>
                <button
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                  className="relative w-16 h-8 bg-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <motion.div
                    className="absolute top-1 left-1 w-6 h-6 bg-blue-500 rounded-full"
                    animate={{ x: billingCycle === 'yearly' ? 32 : 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                </button>
                <span className={`text-lg font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-gray-400'}`}>
                  Yearly
                  <span className="ml-2 text-sm text-green-400">(⭐ Lower Cost per Month ⭐)</span>
                </span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {Object.entries(plans).map(([key, plan]) => {
                const Icon = plan.icon;
                const yearlyPricing = calculateYearlyPrice(plan);
                const price = billingCycle === 'monthly' ? plan.monthlyPrice : yearlyPricing.finalPrice;
                const monthlyEquivalent = billingCycle === 'yearly' ? yearlyPricing.monthlyEquivalent : price;
                
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: key === 'silver' ? 0 : 0.2 }}
                  >
                    <Card className={`glass-effect border-gray-700 card-hover relative h-full ${plan.popular ? 'border-yellow-500 border-2' : ''}`}>
                      {plan.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                            Most Popular
                          </span>
                        </div>
                      )}
                      
                      <CardHeader className="text-center pb-8">
                        <div className={`mx-auto w-16 h-16 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center mb-4`}>
                          <Icon className="h-8 w-8 text-white" />
                        </div>
                        <CardTitle className="text-3xl font-bold text-white mb-2">{plan.name}</CardTitle>
                        <CardDescription className="text-gray-300 text-lg">{plan.description}</CardDescription>
                        
                        <div className="mt-6">
                          {billingCycle === 'yearly' && yearlyPricing.totalDiscountPercent > 0 && (
                            <div className="mb-3">
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-lg text-gray-400 line-through">
                                  ₹{yearlyPricing.originalPrice.toLocaleString('en-IN')}
                                </span>
                                <span className="text-lg font-bold text-green-400">
                                  Save {yearlyPricing.totalDiscountPercent}%
                                </span>
                              </div>
                            </div>
                          )}
                          <div className="flex items-baseline justify-center">
                            <span className="text-5xl font-bold text-white">₹{price.toLocaleString('en-IN')}</span>
                            <span className="text-gray-400 ml-2">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                          </div>
                          {billingCycle === 'yearly' && (
                            <>
                              <p className="text-sm text-gray-400 mt-2">
                                ₹{monthlyEquivalent.toLocaleString('en-IN')}/month billed annually
                              </p>
                              {plan.yearlyPlanDiscount > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-green-400">
                                    Yearly Plan Discount: {plan.yearlyPlanDiscount}% applied
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        <ul className="space-y-4 mb-8">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              {feature.included ? (
                                <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                              ) : (
                                <XCircle className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                              )}
                              <span className={feature.included ? 'text-gray-300' : 'text-gray-600 line-through'}>
                                {feature.name}
                              </span>
                            </li>
                          ))}
                        </ul>
                        
                        <Button
                          onClick={() => handleEnroll(key)}
                          disabled={isSubmitting}
                          className={`w-full py-6 text-lg font-bold ${
                            plan.popular
                              ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white hover:opacity-90'
                              : 'medical-gradient text-white hover:opacity-90'
                          }`}
                          size="lg"
                        >
                          {isSubmitting ? 'Submitting...' : 'Enroll Now'}
                          {!isSubmitting && <Zap className="ml-2 h-5 w-5" />}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            
            {/* Additional Info */}
            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Card className="glass-effect border-gray-700">
                <CardContent className="p-6">
                  <p className="text-gray-300 mb-4">
                    All plans include 24/7 support, regular updates, and secure data storage.
                  </p>
                  <p className="text-gray-400 text-sm">
                    Need help choosing? Contact us at{' '}
                    <a href="mailto:support@dentalhub.com" className="text-blue-400 hover:text-blue-300 underline">
                      support@dentalhub.com
                    </a>
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PricingPage;
